import { annualDayWeight, expandLeaveDays, hoursForLeaveDays } from './accrual'
import { formatDisplayDate } from './dates'
import type { AdditionalHours, LeaveEmail, LeaveEmployee, LeaveRequest, LeaveSettings, PayReportLine } from './types'

export function roundDays(value: number): number {
  return Math.round(value * 100) / 100
}

export function buildPayReportLines(
  employees: LeaveEmployee[],
  requests: LeaveRequest[],
  period: { start: string; end: string },
): PayReportLine[] {
  const lines: PayReportLine[] = []
  requests.forEach((request) => {
    if (request.status !== 'approved') return
    const employee = employees.find((item) => item.id === request.employeeId)
    if (!employee) return
    const days = expandLeaveDays(request, employee.workingDays).filter((day) => day.date >= period.start && day.date <= period.end)
    if (days.length === 0) return
    const dayCount = roundDays(days.reduce((sum, day) => (
      sum + (request.type === 'annual' ? annualDayWeight(employee, day.date, day.portion) : day.portion)
    ), 0))
    lines.push({
      employeeId: employee.id,
      employeeName: employee.name,
      type: request.type,
      startDate: days[0].date,
      endDate: days[days.length - 1].date,
      days: dayCount,
      hours: roundDays(hoursForLeaveDays(employee, days)),
    })
  })
  return lines.sort((a, b) => a.startDate.localeCompare(b.startDate) || a.employeeName.localeCompare(b.employeeName))
}

function formatQuantity(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2)
}

function formatHours(value: number): string {
  return (Math.round(value * 10) / 10).toFixed(1)
}

function leaveTypeLabel(type: PayReportLine['type']): string {
  if (type === 'sick') return 'Sick leave'
  if (type === 'unpaid') return 'Unpaid leave'
  if (type === 'study') return 'Study leave'
  return 'Annual leave'
}

function formatRange(line: PayReportLine): string {
  if (line.startDate === line.endDate) return formatDisplayDate(line.startDate)
  return `${formatDisplayDate(line.startDate)} – ${formatDisplayDate(line.endDate)}`
}

export function payReportTotals(lines: PayReportLine[]): { days: number; hours: number } {
  return {
    days: roundDays(lines.reduce((sum, line) => sum + line.days, 0)),
    hours: roundDays(lines.reduce((sum, line) => sum + line.hours, 0)),
  }
}

function formatReportBody(input: {
  payday: string
  periodStart: string
  periodEnd: string
  lines: PayReportLine[]
  additionalHours?: AdditionalHours[]
  closing: string
}): string {
  const totals = payReportTotals(input.lines)
  const entries = [...input.lines].sort((a, b) => a.employeeName.localeCompare(b.employeeName) || a.startDate.localeCompare(b.startDate))
  const rows = entries.length
    ? entries.map((line) => [
      line.employeeName,
      leaveTypeLabel(line.type),
      formatRange(line),
      `${formatQuantity(line.days)} ${line.days === 1 ? 'day' : 'days'}`,
      `${formatHours(line.hours)} hours`,
    ].join(' · ')).join('\n')
    : 'No approved leave falls in this pay period.'
  const parts = [
    'Leave report',
    `Pay date: ${formatDisplayDate(input.payday)}`,
    `Period: ${formatDisplayDate(input.periodStart)} – ${formatDisplayDate(input.periodEnd)}`,
    '',
    rows,
  ]
  if (entries.length) parts.push('', `Total: ${formatQuantity(totals.days)} days, ${formatHours(totals.hours)} hours`)
  const extra = [...(input.additionalHours || [])].sort((a, b) => a.date.localeCompare(b.date) || a.employeeName.localeCompare(b.employeeName))
  if (extra.length) {
    const extraHours = extra.reduce((sum, item) => sum + item.hours, 0)
    parts.push(
      '',
      'Additional hours worked',
      extra.map((item) => [
        item.employeeName,
        formatDisplayDate(item.date),
        `${formatHours(item.hours)} hours`,
        item.note || '',
      ].filter(Boolean).join(' · ')).join('\n'),
      '',
      `Additional hours total: ${formatHours(extraHours)} hours`,
    )
  }
  parts.push(
    '',
    'Approved leave only. Weekends and other non-working days are not counted. Hours are the employee’s hours for each working day.',
    input.closing,
  )
  return parts.join('\n')
}

export function buildAdminReportEmail(input: {
  payday: string
  periodStart: string
  periodEnd: string
  lines: PayReportLine[]
  additionalHours?: AdditionalHours[]
  settings: LeaveSettings
  createdAt: string
}): LeaveEmail | null {
  const to = input.settings.adminEmails.filter((address) => address.includes('@'))
  if (to.length === 0) return null
  const report = {
    payday: input.payday,
    periodStart: input.periodStart,
    periodEnd: input.periodEnd,
    lines: input.lines,
    additionalHours: input.additionalHours || [],
  }
  return {
    id: `email-admin-${input.createdAt}`,
    kind: 'admin-report',
    to,
    cc: [],
    subject: `Leave report — ${formatDisplayDate(input.payday)}`,
    body: formatReportBody({
      ...report,
      closing: 'Sent by an admin. The accountant has not been emailed by this message.',
    }),
    report,
    createdAt: input.createdAt,
  }
}

export function buildReportReadyEmail(input: {
  payday: string
  dueDate: string
  periodStart: string
  periodEnd: string
  lines: PayReportLine[]
  additionalHours?: AdditionalHours[]
  settings: LeaveSettings
  createdAt: string
}): LeaveEmail {
  const report = {
    payday: input.payday,
    periodStart: input.periodStart,
    periodEnd: input.periodEnd,
    lines: input.lines,
    additionalHours: input.additionalHours || [],
  }
  return {
    id: `email-${input.payday}-ready`,
    kind: 'report-ready',
    to: [...input.settings.adminEmails],
    cc: [],
    subject: `Leave report ready for approval — ${formatDisplayDate(input.payday)}`,
    body: formatReportBody({
      ...report,
      closing: 'Open Team and approve this report before it is sent to the accountant.',
    }),
    report,
    createdAt: input.createdAt,
  }
}

export function approvalEmails(input: {
  status: 'scheduled' | 'pending_approval' | 'sent'
  payday: string
  periodStart: string
  periodEnd: string
  lines: PayReportLine[]
  additionalHours?: AdditionalHours[]
  settings: LeaveSettings
  createdAt: string
}): LeaveEmail[] {
  if (input.status !== 'pending_approval') {
    throw new Error('This pay report is not waiting for approval')
  }
  const report = {
    payday: input.payday,
    periodStart: input.periodStart,
    periodEnd: input.periodEnd,
    lines: input.lines,
    additionalHours: input.additionalHours || [],
  }
  return [{
    id: `email-${input.payday}-sent`,
    kind: 'report-sent',
    to: [input.settings.accountantEmail],
    cc: [...input.settings.adminEmails],
    subject: `Leave report for ${formatDisplayDate(input.payday)}`,
    body: formatReportBody({
      ...report,
      closing: 'Please use this statement for the pay period. A copy has been sent to the clinic admins.',
    }),
    report,
    createdAt: input.createdAt,
  }]
}

function portionLabel(portion: 0.5 | 1): string {
  return portion === 0.5 ? 'half day' : 'full day'
}

export function buildWelcomeEmail(input: {
  name: string
  email: string
  pin: string
  createdAt: string
}): LeaveEmail | null {
  if (!input.email.includes('@')) return null
  return {
    id: `email-welcome-${input.createdAt}`,
    kind: 'welcome',
    to: [input.email],
    cc: [],
    subject: 'Your TFWC team record has been created',
    body: [
      `Hello ${input.name},`,
      '',
      'Your TFWC team record has been created.',
      '',
      `Your PIN is ${input.pin}.`,
      'Open Team and enter this PIN to see your leave and request time off. You can change the PIN after you sign in.',
    ].join('\n'),
    createdAt: input.createdAt,
  }
}

export function buildDecisionEmail(input: {
  requestId: string
  name: string
  email: string
  decision: 'approved' | 'declined'
  type: 'annual' | 'sick' | 'unpaid' | 'study'
  startDate: string
  endDate: string
  startPortion: 0.5 | 1
  endPortion: 0.5 | 1
  note?: string
  decisionNote?: string
  advance?: boolean
  asOf: string
  annualAccrued: number
  annualTaken: number
  annualBooked: number
  annualAvailable: number
  sickEligible: boolean
  sickEligibleFrom: string
  sickAvailable: number
  sickEntitlement: number
  studyAvailable?: number
  studyEntitlement?: number
  studyYearStart?: string
  studyYearEnd?: string
  createdAt: string
}): LeaveEmail | null {
  if (!input.email.includes('@')) return null
  const typeLabel = leaveTypeLabel(input.type)
  const dates = input.startDate === input.endDate
    ? `${formatDisplayDate(input.startDate)} (${portionLabel(input.startPortion)})`
    : `${formatDisplayDate(input.startDate)} (${portionLabel(input.startPortion)}) to ${formatDisplayDate(input.endDate)} (${portionLabel(input.endPortion)})`
  const decision = input.decision === 'approved' ? 'approved' : 'declined'
  const lines = [
    `Hello ${input.name},`,
    '',
    `Your ${typeLabel.toLowerCase()} for ${dates} has been ${decision}.`,
  ]
  if (input.decisionNote) lines.push('', `Note: ${input.decisionNote}`)
  lines.push(
    '',
    'Leave',
    `Type: ${typeLabel}`,
    `Dates: ${dates}`,
  )
  if (input.note) lines.push(`Note: ${input.note}`)
  lines.push(
    '',
    `Your balance on ${formatDisplayDate(input.asOf)}`,
    `Annual leave available: ${formatQuantity(input.annualAvailable)} days`,
    `Accrued this year: ${formatQuantity(input.annualAccrued)} days`,
    `Taken: ${formatQuantity(input.annualTaken)} days`,
    `Booked: ${formatQuantity(input.annualBooked)} days`,
    input.sickEligible
      ? `Statutory sick leave remaining: ${formatQuantity(input.sickAvailable)} of ${formatQuantity(input.sickEntitlement)} days`
      : `Statutory sick leave is available from ${formatDisplayDate(input.sickEligibleFrom)}`,
    typeof input.studyEntitlement === 'number' && input.studyYearStart && input.studyYearEnd
      ? `Study leave remaining: ${formatQuantity(input.studyAvailable || 0)} of ${formatQuantity(input.studyEntitlement)} days for ${formatDisplayDate(input.studyYearStart)} – ${formatDisplayDate(input.studyYearEnd)}. Unused study leave does not carry over.`
      : '',
    '',
    'Unused leave does not carry into the next year.',
  )
  return {
    id: `email-decision-${input.requestId}`,
    kind: 'decision',
    to: [input.email],
    cc: [],
    subject: input.decision === 'approved' ? 'Your leave has been approved' : 'Your leave has been declined',
    body: lines.join('\n'),
    createdAt: input.createdAt,
  }
}

export function buildRequestNoticeEmail(input: {
  requestId: string
  action: 'submitted' | 'cancelled'
  name: string
  type: 'annual' | 'sick' | 'unpaid' | 'study'
  startDate: string
  endDate: string
  startPortion: 0.5 | 1
  endPortion: 0.5 | 1
  note?: string
  exceedsAccrual?: boolean
  settings: LeaveSettings
  createdAt: string
}): LeaveEmail | null {
  const to = input.settings.adminEmails.filter((address) => address.includes('@'))
  if (to.length === 0) return null
  const typeLabel = leaveTypeLabel(input.type)
  const dates = input.startDate === input.endDate
    ? `${formatDisplayDate(input.startDate)} (${portionLabel(input.startPortion)})`
    : `${formatDisplayDate(input.startDate)} (${portionLabel(input.startPortion)}) to ${formatDisplayDate(input.endDate)} (${portionLabel(input.endPortion)})`
  const lines = [
    input.action === 'submitted'
      ? `${input.name} has submitted a leave request for approval.`
      : `${input.name} has cancelled a leave request.`,
    '',
    `Type: ${typeLabel}`,
    `Dates: ${dates}`,
  ]
  if (input.note) lines.push(`Note: ${input.note}`)
  lines.push('', 'Open Team to review it.')
  return {
    id: `email-${input.action}-${input.requestId}`,
    kind: 'request-notice',
    to,
    cc: [],
    subject: input.action === 'submitted'
      ? `Leave request from ${input.name}`
      : `${input.name} cancelled a leave request`,
    body: lines.join('\n'),
    createdAt: input.createdAt,
  }
}

export function buildBalanceEmail(input: {
  name: string
  email: string
  asOf: string
  annualAccrued: number
  annualTaken: number
  annualBooked: number
  annualAvailable: number
  sickAvailable: number
  sickEntitlement: number
  studyAvailable?: number
  studyEntitlement?: number
  createdAt: string
}): LeaveEmail {
  const days = (value: number) => roundDays(value).toString()
  return {
    id: `email-balance-${input.createdAt}`,
    kind: 'balance',
    to: [input.email],
    cc: [],
    subject: `Leave balance for ${input.name}`,
    body: [
      `Leave balance for ${input.name} on ${formatDisplayDate(input.asOf)}.`,
      `Annual leave accrued: ${days(input.annualAccrued)} day(s).`,
      `Annual leave taken: ${days(input.annualTaken)} day(s).`,
      `Annual leave booked: ${days(input.annualBooked)} day(s).`,
      `Annual leave available: ${days(input.annualAvailable)} day(s).`,
      `Statutory sick leave remaining: ${days(input.sickAvailable)} of ${days(input.sickEntitlement)} day(s).`,
      typeof input.studyEntitlement === 'number'
        ? `Study leave remaining: ${days(input.studyAvailable || 0)} of ${days(input.studyEntitlement)} day(s) for this contract year. Unused study leave does not carry over.`
        : '',
      'Unused leave does not carry into the next year.',
    ].join('\n'),
    createdAt: input.createdAt,
  }
}
